import {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
} from "react"
import { flushSync } from "react-dom"
import {
    BookingStepName,
    BookingStep,
    ActionCardState,
} from "types/bookingV2Types"
import { stepConfigurations } from "utils/helpers/bookingv2/stepConfigs"
import {
    shouldShowSelectDayStep,
    shouldShowSelectTimeStep,
    shouldShowSelectSeatStep,
    shouldShowSelectTicketsStep,
    shouldShowAccreditationStep,
    shouldShowAddOnsStep,
    shouldShowCheckoutStep,
} from "utils/helpers/bookingv2/showStepConditions"
import { IFinalEvent } from "types/INewEvent"
import { ItimeSlot } from "types/ticket"
import { isActionCardEnabled } from "utils/helpers/bookingv2/enableActionCardConditions"
import {
    getAvailableTimeslots,
    isTimeslotDisabled,
} from "utils/helpers/bookingv2/ticket"
import { getNextStepAction } from "utils/helpers/bookingv2/navigationActions"
import { useCartState, useCartDispatch } from "hooks/useCartV2"
import { ImprovedCartActionsEnum } from "types/improved-booking"
import { useRouter } from "next/router"
import dayjs from "dayjs"
import { IDayType } from "types/improved-booking"
import { useLang } from "hooks/useLang"

interface BookingV2ContextProps {
    event: IFinalEvent
    tickets: ItimeSlot[]
    currentStep: BookingStep
    hasPrevStep: boolean
    hasNextStep: boolean
    nextStep: {
        action: () => void
        step?: BookingStep
    }
    prevStep: {
        action: () => void
        step?: BookingStep
    }
    actionCard: {
        isTermsAgreementChecked: boolean
        toggleTermsAgreement: () => void
        isActionCardEnabled: boolean
    }
    selectedDateKey: string | null
    setSelectedDateKey: (key: string | null) => void
    navigateToStep: (step: BookingStepName, dateKey?: string) => void
}

const BookingV2Context = createContext<BookingV2ContextProps | undefined>(
    undefined
)

interface BookingProviderV2Props {
    children: ReactNode
    event: IFinalEvent
    tickets: ItimeSlot[]
}

export const BookingProviderV2 = ({
    children,
    event,
    tickets,
}: BookingProviderV2Props) => {
    const router = useRouter()
    const cartState = useCartState()
    const cartDispatch = useCartDispatch()
    const { dynamicTranslate } = useLang()

    const [isTermsAgreementChecked, setIsTermsAgreementChecked] =
        useState<boolean>(false)
    const [currentStepName, setCurrentStepName] = useState<BookingStepName>(
        BookingStepName.SELECT_DAY
    )
    const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null)

    const toggleTermsAgreement = () =>
        setIsTermsAgreementChecked(!isTermsAgreementChecked)

    const availableTimeslots = getAvailableTimeslots(tickets)
    const selectedTickets = cartState.selectedTickets

    // Check if this is an EID party event
    // const isEidPartyEvent = useMemo(() => {
    //     return [
    //         process.env.NEXT_PUBLIC_EID_PARTY_1,
    //         process.env.NEXT_PUBLIC_EID_PARTY_2,
    //         process.env.NEXT_PUBLIC_EID_PARTY_3,
    //     ].includes(event?._id)
    // }, [event?._id])

    // Ensure event ID is set in cart state for use in enableActionCardConditions
    useEffect(() => {
        if (event?._id && cartState.event !== event._id) {
            cartDispatch({
                type: ImprovedCartActionsEnum.ADD_EVENT,
                payload: { event: event._id },
            })
        }
    }, [event?._id, cartState.event, cartDispatch])

    // If there is already a selected day in the cart (e.g. after refresh
    // with persisted cart state), initialize selectedDateKey so that the
    // SelectDay step highlights the correct day card.
    useEffect(() => {
        if (!selectedDateKey && cartState.selectedDays?.start) {
            const existingKey = dayjs(cartState.selectedDays.start).format(
                "YYYY-MM-DD"
            )
            setSelectedDateKey(existingKey)
        }
    }, [cartState.selectedDays, selectedDateKey])

    const availableSteps = [
        shouldShowSelectDayStep(availableTimeslots) &&
            BookingStepName.SELECT_DAY,
        shouldShowSelectTimeStep(selectedDateKey, availableTimeslots) &&
            BookingStepName.SELECT_TIME,
        shouldShowSelectTicketsStep(event) && BookingStepName.SELECT_TICKETS,
        shouldShowSelectSeatStep(event, selectedTickets) &&
            BookingStepName.SELECT_SEAT,
        shouldShowAddOnsStep(event) && BookingStepName.ADD_ONS,
        shouldShowAccreditationStep(event) && BookingStepName.ACCREDITATION,
        shouldShowCheckoutStep(event) && BookingStepName.CHECKOUT,
    ].filter(Boolean) as BookingStepName[]

    // Initialize with the first step or seat selection for EID events with a single time slot
    // const initialStep =
    // isEidPartyEvent && availableTimeslots.length === 1
    //     ? availableSteps.find(
    //           (step) => step === BookingStepName.SELECT_SEAT
    //       ) || availableSteps[0]
    //     : availableSteps[0]

    // Handle single enabled timeslot case (auto-select and advance)
    useEffect(() => {
        const enabledTimeslots = availableTimeslots.filter(
            (slot) => !isTimeslotDisabled(slot)
        )
        if (enabledTimeslots.length === 1 && !cartState.selectedDays) {
            const timeslot = enabledTimeslots[0]
            const currentDate = dayjs(timeslot.start_slot)
            // Map timeslot types to IDayType format with correct data sources
            const mappedTypes: IDayType[] = timeslot.types.map((type) => {
                const id =
                    typeof type.type_id === "string"
                        ? type.type_id
                        : //@ts-ignore
                          type.type_id?._id || ""

                const ticketDetails = event.availableTickets.find(
                    (t) => t._id === id
                )

                return {
                    _id: id,
                    type_id: id,
                    // Display data comes from availableTickets
                    ar_description: ticketDetails?.ar_description ?? "",
                    en_description: ticketDetails?.en_description ?? "",
                    en_title: ticketDetails?.en_title ?? "",
                    ar_title: ticketDetails?.ar_title ?? "",
                    color: ticketDetails?.color ?? "#ffffff",
                    discount: ticketDetails?.discount ?? {
                        current_value: 0,
                        previous_value: 0,
                    },
                    // Quantities come from time_slots.types
                    limit: type.limit,
                    // Min/max come from availableTickets
                    max: ticketDetails?.max ?? 0,
                    min: ticketDetails?.min ?? 1,
                    must_select_seat: ticketDetails?.must_select_seat ?? false,
                    price: ticketDetails?.price ?? 0,
                    group: (ticketDetails as any)?.group,
                    wait_list: type.wait_list,
                }
            })

            const defaultDay = {
                timeslot_id: timeslot._id,
                dayName: currentDate.format("dddd"),
                dayNumber: currentDate.date(),
                monthName: currentDate.format("MMMM"),
                monthNum: currentDate.month() + 1,
                yearNum: currentDate.year().toString(),
                start: new Date(timeslot.start_slot),
                end: new Date(timeslot.end_slot),
                types: mappedTypes,
                seats_season_id: event.seatsio?.season_id || "",
                allow_using_coupon: timeslot.allow_using_coupon,
            }

            cartDispatch({
                type: ImprovedCartActionsEnum.ADD_DAY,
                payload: defaultDay,
            })

            // Ensure selectedDateKey matches this timeslot's day to keep SelectTime filters consistent
            setSelectedDateKey(currentDate.format("YYYY-MM-DD"))

            // Advance to the next step after SELECT_TIME, if present
            const timeIdx = availableSteps.indexOf(BookingStepName.SELECT_TIME)
            const targetStepName =
                timeIdx >= 0 && timeIdx + 1 < availableSteps.length
                    ? availableSteps[timeIdx + 1]
                    : availableSteps[0]
            setCurrentStepName(targetStepName)
        }
    }, [
        availableTimeslots,
        cartState.selectedDays,
        cartDispatch,
        event,
        availableSteps,
    ])

    // NEW EFFECT FOR QUERY PARAM bookingStep
    useEffect(() => {
        const stepMap: Record<string, BookingStepName> = {
            select_day: BookingStepName.SELECT_DAY,
            select_time: BookingStepName.SELECT_TIME,
            select_tickets: BookingStepName.SELECT_TICKETS,
            select_seat: BookingStepName.SELECT_SEAT,
            accreditation: BookingStepName.ACCREDITATION,
            add_ons: BookingStepName.ADD_ONS,
            checkout: BookingStepName.CHECKOUT,
        }

        if (
            router.query.bookingStep &&
            typeof router.query.bookingStep === "string"
        ) {
            const param = router.query.bookingStep.toLowerCase()
            const stepName = stepMap[param]
            if (stepName && availableSteps.includes(stepName)) {
                setCurrentStepName(stepName)
            }
        }
    }, [router.query.bookingStep, availableSteps])

    const currentStepIndex = availableSteps.indexOf(currentStepName)
    const currentStep: BookingStep = stepConfigurations[currentStepName]
    let hasPrevStep = currentStepIndex > 0
    const hasNextStep = currentStepIndex < availableSteps.length - 1

    const prevStepName =
        hasPrevStep &&
        availableSteps[currentStepIndex - 1] === BookingStepName.SELECT_SEAT &&
        currentStepName !== BookingStepName.CHECKOUT
            ? availableSteps[currentStepIndex - 2]
            : availableSteps[currentStepIndex - 1]

    const prevStep: BookingStep | undefined = prevStepName
        ? stepConfigurations[prevStepName]
        : undefined
    const nextStep: BookingStep | undefined = hasNextStep
        ? stepConfigurations[availableSteps[currentStepIndex + 1]]
        : undefined

    const nextStepAction = () => {
        // Check for waitlist tickets requiring authentication
        const hasWaitlistTickets = cartState.selectedTickets?.some(
            (ticket) => ticket.isWaitlist === true
        )

        if (hasWaitlistTickets) {
            // This will be handled by the SelectTickets component's AuthModal
            // The SelectTickets component will intercept this and show auth modal
            // For now, proceed with normal flow - the SelectTickets component will handle the modal
        }

        // Use the current dynamic route param for event identifier so that
        // if a slug is used in the URL, we keep using that slug when
        // navigating to the checkout page. Fall back to event._id only
        // if the route param is not available.
        const eventSlugOrId =
            (router.query.eventid as string | undefined) || event?._id

        return getNextStepAction({
            availableSteps,
            currentStep,
            prevStep,
            nextStep,
            setCurrentStepName,
            router,
            eventId: eventSlugOrId,
        })
    }

    const prevStepAction = () => {
        let newStepIndex = currentStepIndex - 1
        if (newStepIndex >= 0) {
            setCurrentStepName(availableSteps[newStepIndex])
        }
    }

    const navigateToStep = (step: BookingStepName, dateKey?: string) => {
        if (dateKey) {
            flushSync(() => {
                setSelectedDateKey(dateKey)
                setCurrentStepName(step)
            })
        } else {
            setCurrentStepName(step)
        }
    }

    const actionCardState: ActionCardState = {
        currentStepName,
        cartState,
        isTermsAgreementChecked,
    }

    const isEnabled = isActionCardEnabled(
        actionCardState,
        router.pathname,
        dynamicTranslate,
        event
    )

    return (
        <BookingV2Context.Provider
            value={{
                event,
                tickets: availableTimeslots,
                currentStep,
                hasPrevStep,
                hasNextStep,
                nextStep: {
                    action: nextStepAction,
                    step: nextStep,
                },
                prevStep: {
                    action: prevStepAction,
                    step: prevStep,
                },
                actionCard: {
                    isTermsAgreementChecked,
                    toggleTermsAgreement,
                    isActionCardEnabled: isEnabled,
                },
                selectedDateKey,
                setSelectedDateKey,
                navigateToStep,
            }}
        >
            {children}
        </BookingV2Context.Provider>
    )
}

export const useBookingV2 = () => {
    const context = useContext(BookingV2Context)
    if (!context) {
        throw new Error("useBooking must be used within a BookingProviderV2")
    }
    return context
}
